<template>
  <v-icon 
    :size="size" 
    :color="color"
    :class="iconClass"
  >
    {{ getSportIcon(sportKey) }}
  </v-icon>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  sportKey: {
    type: String,
    required: true
  },
  size: {
    type: [String, Number],
    default: 'default'
  },
  color: {
    type: String,
    default: undefined
  },
  variant: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'outlined', 'filled'].includes(value)
  }
})

const iconClass = computed(() => {
  return {
    'sport-icon': true,
    'sport-icon--outlined': props.variant === 'outlined',
    'sport-icon--filled': props.variant === 'filled'
  }
})

const getSportIcon = (sportKey) => {
  // Se sportKey è null, undefined o stringa vuota, ritorna l'icona di default
  if (!sportKey) {
    return 'mdi-trophy-outline'
  }
  
  const sportIcons = {
    // Calcio / Football
    'soccer_epl': 'mdi-soccer',
    'soccer_uefa_champs_league': 'mdi-soccer',
    'soccer_uefa_europa_league': 'mdi-soccer',
    'soccer_italy_serie_a': 'mdi-soccer',
    'soccer_spain_la_liga': 'mdi-soccer',
    'soccer_germany_bundesliga': 'mdi-soccer',
    'soccer_france_ligue_one': 'mdi-soccer',
    'soccer_netherlands_eredivisie': 'mdi-soccer',
    'soccer_portugal_primeira_liga': 'mdi-soccer',
    'soccer_russia_premier_league': 'mdi-soccer',
    'soccer_fifa_world_cup': 'mdi-soccer',
    'soccer_uefa_nations_league': 'mdi-soccer',
    'soccer_conmebol_copa_america': 'mdi-soccer',
    'soccer_brazil_campeonato': 'mdi-soccer',
    'soccer_argentina_primera_division': 'mdi-soccer',
    'soccer_mexico_ligamx': 'mdi-soccer',
    'soccer_mls': 'mdi-soccer',
    'soccer_league_one': 'mdi-soccer',
    'soccer_efl_champ': 'mdi-soccer',
    'soccer_fa_cup': 'mdi-soccer',
    'soccer_league_cup': 'mdi-soccer',
    
    // Football Americano
    'americanfootball_nfl': 'mdi-football',
    'americanfootball_ncaaf': 'mdi-football',
    'americanfootball_cfl': 'mdi-football',
    
    // Basketball
    'basketball_nba': 'mdi-basketball',
    'basketball_ncaab': 'mdi-basketball',
    'basketball_wnba': 'mdi-basketball',
    'basketball_euroleague': 'mdi-basketball',
    'basketball_italy_lega_basket_serie_a': 'mdi-basketball',
    'basketball_spain_acb': 'mdi-basketball',
    'basketball_germany_bbl': 'mdi-basketball',
    'basketball_france_lnb': 'mdi-basketball',
    
    // Baseball
    'baseball_mlb': 'mdi-baseball',
    'baseball_ncaa': 'mdi-baseball',
    'baseball_kbo': 'mdi-baseball',
    'baseball_npb': 'mdi-baseball',
    
    // Hockey
    'icehockey_nhl': 'mdi-hockey-puck',
    'icehockey_sweden_hockey_league': 'mdi-hockey-puck',
    'icehockey_finland_liiga': 'mdi-hockey-puck',
    'icehockey_switzerland_nl': 'mdi-hockey-puck',
    'icehockey_germany_del': 'mdi-hockey-puck',
    'icehockey_khl': 'mdi-hockey-puck',
    
    // Tennis
    'tennis_atp_french_open': 'mdi-tennis',
    'tennis_atp_wimbledon': 'mdi-tennis',
    'tennis_atp_us_open': 'mdi-tennis',
    'tennis_atp_australian_open': 'mdi-tennis',
    'tennis_wta_french_open': 'mdi-tennis',
    'tennis_wta_wimbledon': 'mdi-tennis',
    'tennis_wta_us_open': 'mdi-tennis',
    'tennis_wta_australian_open': 'mdi-tennis',
    
    // Golf
    'golf_pga_championship': 'mdi-golf',
    'golf_masters_tournament': 'mdi-golf',
    'golf_us_open': 'mdi-golf',
    'golf_the_open_championship': 'mdi-golf',
    
    // Boxing / MMA
    'boxing_heavyweight': 'mdi-boxing-glove',
    'mma_mixed_martial_arts': 'mdi-karate',
    'ufc': 'mdi-karate',
    
    // Motorsports
    'motorsport_f1': 'mdi-racing-helmet',
    'motorsport_motogp': 'mdi-motorbike',
    'motorsport_nascar': 'mdi-car-sports',
    'motorsport_indycar': 'mdi-car-sports',
    
    // Cricket
    'cricket_icc_world_cup': 'mdi-cricket',
    'cricket_ipl': 'mdi-cricket',
    'cricket_big_bash': 'mdi-cricket',
    'cricket_county_championship': 'mdi-cricket',
    
    // Rugby
    'rugbyunion_world_cup': 'mdi-rugby',
    'rugbyunion_six_nations': 'mdi-rugby',
    'rugbyunion_premiership': 'mdi-rugby',
    'rugbyleague_nrl': 'mdi-rugby',
    
    // Aussie Rules
    'aussierules_afl': 'mdi-football-australian',
    
    // Olympics
    'olympics_mens': 'mdi-medal',
    'olympics_womens': 'mdi-medal',
    
    // Esports
    'esports_lol': 'mdi-gamepad-variant',
    'esports_csgo': 'mdi-gamepad-variant',
    'esports_dota2': 'mdi-gamepad-variant',
    'esports_valorant': 'mdi-gamepad-variant',
    'esports_overwatch': 'mdi-gamepad-variant',
    
    // Volleyball
    'volleyball': 'mdi-volleyball',
    
    // Handball
    'handball': 'mdi-handball',
    
    // Water Polo
    'waterpolo': 'mdi-water-polo',
    
    // Darts
    'darts': 'mdi-dart',
    
    // Snooker
    'snooker': 'mdi-billiards',
    
    // Table Tennis
    'table_tennis': 'mdi-table-tennis',
    
    // Badminton
    'badminton': 'mdi-badminton',
    
    // Politics
    'politics_us_presidential_election': 'mdi-vote',
    'politics_us_senate': 'mdi-vote',
    'politics_us_house': 'mdi-vote',
    'politics_uk_general_election': 'mdi-vote'
  }
  
  // Se la chiave esatta non viene trovata, prova a fare un match parziale
  if (sportIcons[sportKey]) {
    return sportIcons[sportKey]
  }
  
  // Match parziali per categorie generali
  if (sportKey.includes('soccer') || sportKey.includes('football') && !sportKey.includes('american')) {
    return 'mdi-soccer'
  }
  if (sportKey.includes('americanfootball')) {
    return 'mdi-football'
  }
  if (sportKey.includes('basketball')) {
    return 'mdi-basketball'
  }
  if (sportKey.includes('baseball')) {
    return 'mdi-baseball'
  }
  if (sportKey.includes('hockey') || sportKey.includes('icehockey')) {
    return 'mdi-hockey-puck'
  }
  if (sportKey.includes('tennis')) {
    return 'mdi-tennis'
  }
  if (sportKey.includes('golf')) {
    return 'mdi-golf'
  }
  if (sportKey.includes('boxing')) {
    return 'mdi-boxing-glove'
  }
  if (sportKey.includes('mma') || sportKey.includes('ufc')) {
    return 'mdi-karate'
  }
  if (sportKey.includes('motorsport') || sportKey.includes('f1') || sportKey.includes('racing')) {
    return 'mdi-racing-helmet'
  }
  if (sportKey.includes('cricket')) {
    return 'mdi-cricket'
  }
  if (sportKey.includes('rugby')) {
    return 'mdi-rugby'
  }
  if (sportKey.includes('volleyball')) {
    return 'mdi-volleyball'
  }
  if (sportKey.includes('esports') || sportKey.includes('gaming')) {
    return 'mdi-gamepad-variant'
  }
  if (sportKey.includes('olympics')) {
    return 'mdi-medal'
  }
  if (sportKey.includes('politics') || sportKey.includes('election')) {
    return 'mdi-vote'
  }
  
  // Icona di default per sport non riconosciuti
  return 'mdi-trophy-outline'
}
</script>

<style scoped>
.sport-icon {
  transition: all 0.2s ease;
}

.sport-icon--outlined {
  border: 1px solid currentColor;
  border-radius: 50%;
  padding: 4px;
}

.sport-icon--filled {
  background-color: currentColor;
  color: white;
  border-radius: 50%;
  padding: 4px;
}

.sport-icon:hover {
  transform: scale(1.1);
}
</style>